import { useEffect, useState } from "react";
import { orderBy, where } from "firebase/firestore";
import type { DeliveryMethod, DeliveryOf, StoreCheckin } from "@gull/shared";
import { subscribeDeliveries } from "../../../lib/deliveries";
import { checkinDelivery, mailOrderDelivery, type Delivery } from "../delivery";

/**
 * All three streams below are the same query — one uid field, newest first — so they are one hook
 * taking that field by name. `null` until the first snapshot lands, so callers can tell "none" from
 * "not read yet".
 *
 * The caller names the delivery method and nothing else; `lib/deliveries` turns that into the
 * collection and the materialised rows.
 */
function useDeliveryStream<M extends DeliveryMethod>(
  method: M,
  uidField: string,
  uid: string,
): DeliveryOf<M>[] | null {
  const [rows, setRows] = useState<DeliveryOf<M>[] | null>(null);

  useEffect(() => {
    setRows(null);
    return subscribeDeliveries(
      method,
      [where(uidField, "==", uid), orderBy("createdAt", "desc")],
      setRows,
    );
  }, [method, uidField, uid]);

  return rows;
}

/**
 * Every delivery the customer can see, newest first: their mail orders, the check-ins they own, and
 * the check-ins they later claimed with a receipt code. A claimed check-in can appear on both
 * check-in streams at once, so they are merged by id before mapping.
 */
export function useDeliveries(uid: string): Delivery[] | null {
  const orders = useDeliveryStream("MAIL", "customerUid", uid);
  const owned = useDeliveryStream("STORE", "customerUid", uid);
  const claimed = useDeliveryStream("STORE", "claimedByUid", uid);

  if (orders === null || owned === null || claimed === null) return null;

  const checkins = new Map<string, StoreCheckin>();
  for (const checkin of [...owned, ...claimed]) checkins.set(checkin.id, checkin);

  return [...orders.map(mailOrderDelivery), ...Array.from(checkins.values(), checkinDelivery)].sort(
    (a, b) => b.createdAt - a.createdAt,
  );
}
